import { MAX_UPLOAD_BYTES } from '../../shared/files';
import { useEffect, useState } from 'preact/hooks';
import {
  checkLimit,
  type ClaimBody,
  type ClaimFormBody,
  type ClaimSummary,
  type PcAction,
  type PettyCashBody,
} from '../../shared/petty/petty';
import { DateHint } from '../components/DateHint';
import { useI18n, type MessageKey } from '../i18n';
import { ApiError, apiDownload, apiGet, apiSend, apiUpload } from '../lib/api';
import { formatDate } from '../lib/dates';

// Petty cash (P4.7, screen 18; 01-SPEC §5, D63). A claim for something already paid, with a
// receipt, up to the limit per claim; above it the screen and the broker both point to a PR.
// Finance (never the claimant) approves, returns or rejects, then records the reimbursement.

type Load<T> =
  | { kind: 'loading' }
  | { kind: 'error'; code: string; reference: string | null }
  | { kind: 'ok'; data: T };

const money = (minor: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    minor / 100,
  );
const whole = (minor: number) =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(minor / 100);
const MAX_FILE = MAX_UPLOAD_BYTES;
const today = () => new Date().toISOString().slice(0, 10);

const PILL: Record<string, string> = {
  Draft: 'pill--neutral',
  WithFinance: 'pill--info',
  Returned: 'pill--warning',
  Approved: 'pill--info',
  Reimbursed: 'pill--success',
  Rejected: 'pill--danger',
};

function useLoad<T>(path: `/api/${string}`, reload: number): Load<T> {
  const [load, setLoad] = useState<Load<T>>({ kind: 'loading' });
  useEffect(() => {
    let live = true;
    apiGet<T>(path)
      .then((data) => live && setLoad({ kind: 'ok', data }))
      .catch((err: unknown) => {
        if (!live) return;
        const e = err instanceof ApiError ? err : null;
        setLoad({ kind: 'error', code: e?.code ?? 'generic', reference: e?.correlationId ?? null });
      });
    return () => {
      live = false;
    };
  }, [path, reload]);
  return load;
}

function Status<T>({ load }: { load: Exclude<Load<T>, { kind: 'ok' }> }) {
  const { t } = useI18n();
  if (load.kind === 'loading') {
    return (
      <section class="card" aria-busy="true">
        <p>{t('req.loading')}</p>
      </section>
    );
  }
  return (
    <section class="card card--error" role="alert">
      <p>{load.code === 'not_found' ? t('pc.error.notFound') : t('req.loadError')}</p>
      {load.reference && (
        <p class="caption" dir="ltr">
          {t('error.reference', { ref: load.reference })}
        </p>
      )}
    </section>
  );
}

function Problem({
  note,
}: {
  note: { ok: boolean; text: string; ref?: string | null; pr?: string };
}) {
  const { t } = useI18n();
  return (
    <div class={note.ok ? 'form-saved' : 'form-problem'} role={note.ok ? 'status' : 'alert'}>
      <p>
        {note.text}
        {note.pr && (
          <>
            {' '}
            <a href={`#/requests/${note.pr}`}>{t('pc.useRfq')}</a>
          </>
        )}
      </p>
      {note.ref && (
        <p class="caption" dir="ltr">
          {t('error.reference', { ref: note.ref })}
        </p>
      )}
    </div>
  );
}

interface Form {
  description: string;
  amount: string;
  paidOn: string;
  paidTo: string;
}

interface Existing {
  pcNo: string;
  etag: string;
  receipt: string;
  form: Form;
}

/** The buyer's claim form, for a new claim on a request or a draft / returned one. */
function ClaimForm({
  prNo,
  title,
  currency,
  limit,
  existing,
}: {
  prNo: string;
  title: string;
  currency: string;
  limit: number;
  existing?: Existing;
}) {
  const { t } = useI18n();
  const [form, setForm] = useState<Form>(
    existing?.form ?? { description: title, amount: '', paidOn: today(), paidTo: '' },
  );
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{
    ok: boolean;
    text: string;
    ref?: string | null;
    pr?: string;
  } | null>(null);

  const amount = Math.round(Number(form.amount || '0') * 100);
  const limitCheck = checkLimit(Number.isFinite(amount) ? amount : 0, limit, currency);
  const set = (patch: Partial<Form>) => setForm({ ...form, ...patch });
  const limitText = (reason: 'over_limit' | 'no_limit') =>
    reason === 'no_limit'
      ? t('pc.err.noLimit', { currency })
      : t('pc.err.overLimit', { currency, limit: whole(limit) });

  async function save(submit: boolean) {
    const errs: Record<string, string> = {};
    if (!form.description.trim()) errs.description = t('pc.err.required');
    const n = Number(form.amount);
    if (!form.amount.trim() || !Number.isFinite(n) || n <= 0) errs.amount = t('pc.err.amount');
    if (!form.paidOn) errs.paidOn = t('pc.err.required');
    else if (form.paidOn > today()) errs.paidOn = t('pc.err.future');
    if (!form.paidTo.trim()) errs.paidTo = t('pc.err.required');
    if (file && file.size > MAX_FILE) errs.receipt = t('dl.err.fileSize');
    if (submit && !file && !existing?.receipt) errs.receipt = t('pc.err.receipt');
    setErrors(errs);
    if (!limitCheck.ok) {
      setNote({ ok: false, text: limitText(limitCheck.reason), pr: prNo });
      return;
    }
    if (Object.keys(errs).length > 0) {
      setNote({ ok: false, text: t('dl.err.fix') });
      return;
    }
    const claim = {
      description: form.description.trim(),
      amount: n,
      paidOn: form.paidOn,
      paidTo: form.paidTo.trim(),
    };
    setBusy(true);
    setNote(null);
    let pcNo = existing?.pcNo;
    try {
      let etag = existing?.etag ?? '';
      if (!pcNo) {
        const made = await apiSend<{ pcNo: string; etag: string }>(
          'POST',
          `/api/prs/${encodeURIComponent(prNo)}/petty-cash`,
          { action: 'claim', claim },
        );
        pcNo = made.pcNo;
        etag = made.etag;
      }
      const base = `/api/petty-cash/${encodeURIComponent(pcNo)}` as const;
      if (file) etag = (await apiUpload<{ etag: string }>(`${base}/receipt`, file)).etag;
      if (existing || submit) await apiSend('PATCH', base, { etag, claim, submit });
      location.hash = `#/petty-cash/${pcNo}${submit ? '?sent=1' : '?saved=1'}`;
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      const code = e?.code ?? '';
      setErrors(
        Object.fromEntries(Object.keys(e?.fields ?? {}).map((k) => [k, t('fin.err.field')])),
      );
      setNote({
        ok: false,
        text:
          code === 'over_limit' || code === 'no_limit'
            ? limitText(code)
            : code === 'claim_exists'
              ? t('pc.err.claimExists')
              : code === 'receipt_required'
                ? t('pc.err.receipt')
                : code === 'conflict'
                  ? t('pc.err.conflict')
                  : code === 'invalid_input'
                    ? t('dl.err.fix')
                    : pcNo && !existing
                      ? t('pc.err.partSaved', { no: pcNo })
                      : t('dl.err.save'),
        ref: e?.correlationId ?? null,
        ...(code === 'over_limit' || code === 'no_limit' ? { pr: prNo } : {}),
      });
    } finally {
      setBusy(false);
    }
  }

  const input = (
    id: keyof Form,
    label: MessageKey,
    o: { type?: string; dir?: 'ltr' | 'auto'; max?: number; mode?: 'decimal' } = {},
  ) => (
    <div class="field">
      <label for={`pc-${id}`}>{t(label, { currency })}</label>
      <input
        id={`pc-${id}`}
        type={o.type ?? 'text'}
        dir={o.dir ?? 'auto'}
        inputMode={o.mode}
        maxLength={o.max ?? 120}
        max={o.type === 'date' ? today() : undefined}
        value={form[id]}
        aria-invalid={errors[id] ? true : undefined}
        aria-describedby={errors[id] ? `pc-${id}-err` : undefined}
        onInput={(e) => set({ [id]: (e.target as HTMLInputElement).value })}
      />
      {errors[id] && (
        <p class="field__error" id={`pc-${id}-err`}>
          {errors[id]}
        </p>
      )}
    </div>
  );

  return (
    <section class="card stack pc-form" aria-labelledby="pc-form">
      <h2 id="pc-form" class="visually-hidden">
        {t('pc.title')}
      </h2>
      {note && <Problem note={note} />}
      {input('description', 'pc.description', { max: 200 })}
      <div class="inv-grid">
        {input('amount', 'pc.amount', { dir: 'ltr', mode: 'decimal', max: 14 })}
        {input('paidOn', 'pc.paidOn', { type: 'date', dir: 'ltr' })}
      </div>
      <div class="pc-limit">
        <div class="pv-row caption">
          <span>{t('pc.limitUsed')}</span>
          <span dir="ltr">
            {limit > 0 ? `${whole(Math.max(0, amount || 0))} / ${whole(limit)}` : '—'}
          </span>
        </div>
        <progress
          class={`progress${limitCheck.ok ? '' : ' progress--over'}`}
          max={Math.max(limit, 1)}
          value={Math.min(Math.max(amount || 0, 0), Math.max(limit, 1))}
          aria-label={t('pc.limitUsed')}
        />
        <p class={limitCheck.ok ? 'caption' : 'field__error'} aria-live="polite">
          {limitCheck.ok ? t('pc.aboveHint') : limitText(limitCheck.reason)}{' '}
          <a href={`#/requests/${prNo}`}>{t('pc.useRfq')}</a>
        </p>
      </div>
      {input('paidTo', 'pc.paidTo')}
      <div class="field">
        <label for="pc-receipt">
          {t('pc.receipt')} <span class="caption">{t('pc.required')}</span>
        </label>
        {existing?.receipt && !file && (
          <p class="caption" dir="ltr">
            {existing.receipt}
          </p>
        )}
        <input
          id="pc-receipt"
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          aria-describedby="pc-receipt-hint"
          aria-invalid={errors.receipt ? true : undefined}
          onChange={(e) => setFile((e.target as HTMLInputElement).files?.[0] ?? null)}
        />
        <p class="caption" id="pc-receipt-hint">
          {existing?.receipt ? t('pc.replaceHint') : t('dl.fileHint')}
        </p>
        {errors.receipt && <p class="field__error">{errors.receipt}</p>}
      </div>
      <div class="actions">
        <button type="button" class="button" disabled={busy} onClick={() => void save(false)}>
          {t('pc.saveDraft')}
        </button>
        <button
          type="button"
          class="button button--primary"
          disabled={busy || !limitCheck.ok}
          onClick={() => void save(true)}
        >
          {busy ? t('dl.saving') : t('pc.submit')}
        </button>
      </div>
    </section>
  );
}

function ClaimList({ items, finance }: { items: ClaimSummary[]; finance?: boolean }) {
  const { t, lang } = useI18n();
  if (items.length === 0)
    return <p class="caption">{finance ? t('fin.none') : t('pc.noClaims')}</p>;
  return (
    <ul class="action-list">
      {items.map((c) => (
        <li key={c.pcNo}>
          <a class="cfo-row" href={`#/petty-cash/${c.pcNo}`}>
            <span class="req-row__title" dir="auto">
              {c.description || c.pcNo}
            </span>
            <span class="caption cell-sub">
              <span dir="ltr">{c.pcNo}</span>
              {c.prNo && (
                <>
                  {' · '}
                  <span dir="ltr">{c.prNo}</span>
                </>
              )}
              {finance && c.requesterName && ` · ${c.requesterName}`}
              {c.paidOn && ` · ${formatDate(c.paidOn, lang)}`}
            </span>
            <span class="cfo-row__amount" dir="ltr">
              {c.currency} {money(c.amount)}
              <span class={`pill ${PILL[c.status] ?? 'pill--neutral'} pc-pill`}>
                {t(`pc.status.${c.status}` as MessageKey)}
              </span>
            </span>
          </a>
        </li>
      ))}
    </ul>
  );
}

function FinanceLists({ data }: { data: NonNullable<PettyCashBody['finance']> }) {
  const { t } = useI18n();
  return (
    <>
      {!data.canAct && <p class="notice notice--info">{t('pc.readOnly')}</p>}
      <section class="card" aria-labelledby="pc-fin-approve">
        <h2 id="pc-fin-approve" class="card__title">
          {t('pc.toApprove', { n: String(data.toApprove.length) })}
        </h2>
        <ClaimList items={data.toApprove} finance />
      </section>
      <section class="card" aria-labelledby="pc-fin-pay">
        <h2 id="pc-fin-pay" class="card__title">
          {t('pc.toReimburse', { n: String(data.toReimburse.length) })}
        </h2>
        <ClaimList items={data.toReimburse} finance />
      </section>
    </>
  );
}

/** Petty cash (D65): Procurement's requests to claim and claims; Finance's lists to act on. */
export function PettyCashPage({ procurement }: { procurement: boolean }) {
  const { t } = useI18n();
  const load = useLoad<PettyCashBody>('/api/petty-cash', 0);
  if (load.kind !== 'ok') return <Status load={load} />;
  const data = load.data;
  const sar = data.limits.SAR ?? 0;
  return (
    <div class="stack">
      <p class="caption">{sar > 0 ? t('pc.intro', { limit: whole(sar) }) : t('pc.introNoLimit')}</p>
      {procurement && (
        <div class="pv-layout">
          <section class="card" aria-labelledby="pc-waiting">
            <h2 id="pc-waiting" class="card__title">
              {t('pc.waiting', { n: String(data.waiting.length) })}
            </h2>
            {data.waiting.length === 0 ? (
              <p class="caption">{t('pc.noWaiting')}</p>
            ) : (
              <ul class="action-list">
                {data.waiting.map((w) => (
                  <li key={w.prNo}>
                    <a class="cfo-row" href={`#/requests/${w.prNo}/petty-cash`}>
                      <span class="req-row__title" dir="auto">
                        {w.title || w.prNo}
                      </span>
                      <span class="caption cell-sub">
                        <span dir="ltr">{w.prNo}</span> · {w.requesterName} · {w.department}
                      </span>
                      <span class="cfo-row__amount pc-cta">{t('pc.makeClaim')}</span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <aside class="pv-side">
            <section class="card" aria-labelledby="pc-mine">
              <h2 id="pc-mine" class="card__title">
                {t('pc.mine')}
              </h2>
              <ClaimList items={data.mine} />
            </section>
          </aside>
        </div>
      )}
      {data.finance && <FinanceLists data={data.finance} />}
    </div>
  );
}

/** The claim form on a request Procurement set to petty cash (`#/requests/<PR>/petty-cash`). */
export function PettyCashClaimFormPage({ prNo }: { prNo: string }) {
  const { t } = useI18n();
  const load = useLoad<ClaimFormBody>(`/api/prs/${encodeURIComponent(prNo)}/petty-cash`, 0);
  if (load.kind !== 'ok') return <Status load={load} />;
  const d = load.data;
  return (
    <div class="stack">
      <p class="caption">
        <a href="#/petty-cash">{t('nav.pettyCash')}</a> /{' '}
        <a href={`#/requests/${prNo}`} dir="ltr">
          {prNo}
        </a>
      </p>
      <h2 class="detail-title" dir="auto">
        {d.title}
      </h2>
      <p class="caption">{t('pc.formIntro')}</p>
      <ClaimForm prNo={prNo} title={d.title} currency={d.currency} limit={d.limit} />
    </div>
  );
}

/** Finance's decisions on a claim: approve, return or reject with a remark, then reimburse. */
function FinancePanel({ body, onDone }: { body: ClaimBody; onDone: (text: string) => void }) {
  const { t } = useI18n();
  const [remark, setRemark] = useState('');
  const [ref, setRef] = useState('');
  const [on, setOn] = useState(today());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<{ text: string; ref: string | null } | null>(null);
  const can = (a: PcAction) => body.actions.includes(a);

  async function act(payload: Record<string, string>) {
    setBusy(true);
    setProblem(null);
    try {
      await apiSend('POST', `/api/petty-cash/${encodeURIComponent(body.claim.pcNo)}/finance`, {
        ...payload,
        etag: body.claim.etag,
      });
      onDone(t(`pc.done.${payload.action}` as MessageKey));
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      setErrors(
        Object.fromEntries(Object.keys(e?.fields ?? {}).map((k) => [k, t('fin.err.field')])),
      );
      setProblem({
        text:
          e?.code === 'self_approval'
            ? t('pc.err.own')
            : e?.code === 'conflict' || e?.code === 'invalid_transition'
              ? t('pc.err.conflict')
              : t('dl.err.save'),
        ref: e?.correlationId ?? null,
      });
    } finally {
      setBusy(false);
    }
  }

  const withRemark = (action: 'return' | 'reject') => {
    if (!remark.trim()) {
      setErrors({ remark: t('pc.err.remark') });
      return;
    }
    void act({ action, remark: remark.trim() });
  };

  return (
    <section class="card stack" aria-labelledby="pc-fin">
      <h3 id="pc-fin" class="card__title">
        {t('fin.title')}
      </h3>
      {problem && <Problem note={{ ok: false, ...problem }} />}
      {can('approve') && (
        <button
          type="button"
          class="button button--primary"
          disabled={busy}
          onClick={() => void act({ action: 'approve' })}
        >
          {t('pc.approve')}
        </button>
      )}
      {can('reimburse') && (
        <div class="stack">
          <div class="field">
            <label for="pc-ref">{t('pc.reference')}</label>
            <input
              id="pc-ref"
              dir="ltr"
              maxLength={60}
              value={ref}
              aria-invalid={errors.reference ? true : undefined}
              onInput={(e) => setRef((e.target as HTMLInputElement).value)}
            />
            {errors.reference && <p class="field__error">{errors.reference}</p>}
          </div>
          <div class="field">
            <label for="pc-on">{t('pc.reimbursedOn')}</label>
            <input
              id="pc-on"
              type="date"
              dir="ltr"
              max={today()}
              value={on}
              aria-invalid={errors.reimbursedOn ? true : undefined}
              aria-describedby="pc-on-when"
              onInput={(e) => setOn((e.target as HTMLInputElement).value)}
            />
            <DateHint id="pc-on-when" value={on} />
            {errors.reimbursedOn && <p class="field__error">{errors.reimbursedOn}</p>}
          </div>
          <button
            type="button"
            class="button button--primary"
            disabled={busy}
            onClick={() => {
              if (!ref.trim()) {
                setErrors({ reference: t('pc.err.reference') });
                return;
              }
              void act({ action: 'reimburse', reference: ref.trim(), reimbursedOn: on });
            }}
          >
            {t('pc.reimburse')}
          </button>
        </div>
      )}
      {can('return') && (
        <div class="stack">
          <div class="field">
            <label for="pc-remark">{t('pc.remark')}</label>
            <textarea
              id="pc-remark"
              rows={3}
              maxLength={1000}
              dir="auto"
              value={remark}
              aria-invalid={errors.remark ? true : undefined}
              onInput={(e) => setRemark((e.target as HTMLTextAreaElement).value)}
            />
            {errors.remark && <p class="field__error">{errors.remark}</p>}
          </div>
          <div class="actions">
            <button
              type="button"
              class="button"
              disabled={busy}
              onClick={() => withRemark('return')}
            >
              {t('pc.return')}
            </button>
            <button
              type="button"
              class="button button--danger"
              disabled={busy}
              onClick={() => withRemark('reject')}
            >
              {t('pc.reject')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function Field({ label, children }: { label: string; children: preact.ComponentChildren }) {
  return (
    <div class="pv-field">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/** One claim: the form while it is the claimant's draft or returned, else the record. */
export function PettyCashClaimPage({ pcNo, flash }: { pcNo: string; flash?: string | undefined }) {
  const { t, lang } = useI18n();
  const [reload, setReload] = useState(0);
  const [note, setNote] = useState<{ ok: boolean; text: string; ref?: string | null } | null>(
    flash === 'sent'
      ? { ok: true, text: t('pc.done.sent') }
      : flash === 'saved'
        ? { ok: true, text: t('pc.done.saved') }
        : null,
  );
  const load = useLoad<ClaimBody>(`/api/petty-cash/${encodeURIComponent(pcNo)}`, reload);
  if (load.kind !== 'ok') return <Status load={load} />;
  const { claim: c } = load.data;

  async function download() {
    try {
      await apiDownload(`/api/petty-cash/${encodeURIComponent(pcNo)}/receipt`, `${pcNo} receipt`);
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      setNote({ ok: false, text: t('pc.err.receiptMissing'), ref: e?.correlationId ?? null });
    }
  }

  return (
    <div class="stack">
      <p class="caption">
        <a href="#/petty-cash">{t('nav.pettyCash')}</a> /{' '}
        {c.prNo && (
          <>
            <a href={`#/requests/${c.prNo}`} dir="ltr">
              {c.prNo}
            </a>{' '}
            /{' '}
          </>
        )}
        <span dir="ltr">{pcNo}</span>
      </p>
      <div class="pv-row">
        <h2 class="detail-title" dir="auto">
          {c.description}
        </h2>
        <span class={`pill ${PILL[c.status] ?? 'pill--neutral'}`}>
          {t(`pc.status.${c.status}` as MessageKey)}
        </span>
      </div>
      {note && <Problem note={note} />}
      {c.financeRemark && (c.status === 'Returned' || c.status === 'Rejected') && (
        <div class="notice notice--action" role="note">
          <p>
            <strong>{c.status === 'Returned' ? t('pc.returnedBy') : t('pc.rejectedBy')}</strong>{' '}
            <span dir="auto">{c.financeRemark}</span>
          </p>
        </div>
      )}
      {load.data.canEdit ? (
        <ClaimForm
          key={c.etag}
          prNo={c.prNo}
          title={c.description}
          currency={c.currency}
          limit={load.data.limit}
          existing={{
            pcNo,
            etag: c.etag,
            receipt: c.receipt,
            form: {
              description: c.description,
              amount: String(c.amount / 100),
              paidOn: c.paidOn,
              paidTo: c.paidTo,
            },
          }}
        />
      ) : (
        <div class="pv-layout">
          <section class="card" aria-labelledby="pc-claim">
            <h3 id="pc-claim" class="card__title">
              {t('pc.claim')}
            </h3>
            <dl class="pv-fields">
              <Field label={t('pc.amountLabel')}>
                <span dir="ltr">
                  {c.currency} {money(c.amount)}
                </span>
              </Field>
              <Field label={t('pc.paidOn')}>{formatDate(c.paidOn, lang)}</Field>
              <Field label={t('pc.paidTo')}>
                <span dir="auto">{c.paidTo}</span>
              </Field>
              <Field label={t('pc.costCentre')}>
                {c.department} · {c.vertical}
              </Field>
              <Field label={t('pc.claimant')}>{c.requesterName}</Field>
              <Field label={t('pc.entityOffice')}>
                <span dir="ltr">
                  {c.entity} · {c.office}
                </span>
              </Field>
              {c.submittedAt && (
                <Field label={t('pc.submittedAt')}>{formatDate(c.submittedAt, lang)}</Field>
              )}
              {c.financeApprover && (
                <Field label={t('pc.decidedBy')}>
                  <span dir="ltr">{c.financeApprover}</span>
                </Field>
              )}
              {c.reimbursedOn && (
                <Field label={t('pc.reimbursedOn')}>
                  {formatDate(c.reimbursedOn, lang)} · <span dir="ltr">{c.reimbursementRef}</span>
                </Field>
              )}
              <Field label={t('pc.receipt')}>
                {c.receipt ? (
                  <>
                    <span dir="ltr">{c.receipt}</span>{' '}
                    <button
                      type="button"
                      class="button button--small"
                      onClick={() => void download()}
                    >
                      {t('sup.docs.download')}
                    </button>
                  </>
                ) : (
                  '—'
                )}
              </Field>
            </dl>
          </section>
          {load.data.actions.length > 0 && (
            <aside class="pv-side">
              <FinancePanel
                body={load.data}
                onDone={(text) => {
                  setNote({ ok: true, text });
                  setReload((r) => r + 1);
                }}
              />
            </aside>
          )}
        </div>
      )}
    </div>
  );
}
